import { EAttachmentType, EAttachmentUploadStatus } from "api/enums";
import {
  InvalidAttachmentException,
  InvalidAttachmentFormatException,
  MaximumAttachmentSizeException,
} from "api/exceptions/AttachmentException";
import AttachmentModel from "api/models/AttachmentModel";
import ContractModel from "api/models/ContractModel";
import AttachmentRepository from "api/repositories/AttachmentRepository";
import axios from "axios";
import { Transaction } from "firebase/firestore";
import JSZip from "jszip";
import { PDFDocument } from "pdf-lib";
import { uuidFirebase } from "utils";
import ContractRowMovimentService from "./ContractRowMovimentService";
import ExcelService from "./ExcelService";
import FileService from "./FileService";
import TransactionService from "./TransactionService";

const jpgMime = "image/jpeg";
const pdfMime = "application/pdf";
const txtMime = "text/plain";
const docxMime =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const xlsxMime =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

class AttachmentService {
  public async getAttachmentsByIds(
    contractUid: string,
    attachmentsIds: string[]
  ) {
    const attachments: AttachmentModel[] = [];
    let ids = [...attachmentsIds];

    while (ids.length) {
      const partialIds = ids.slice(0, 10);
      ids = ids.slice(10);

      const list = await AttachmentRepository.getAttachmentsFromDataBase(
        contractUid,
        partialIds
      );
      attachments.push(...list);
    }

    return attachments;
  }

  public uploadAttachmentToStorage(
    contractUid: string,
    file: File,
    attachedTo: EAttachmentType,
    onError: (error: string) => void,
    onFinish?: () => void
  ): AttachmentModel {
    const attachment = this.createAttachmentFromFile(file, attachedTo);
    const filePath = this.getFilePath(contractUid, attachment);

    FileService.uploadWithProgress(
      file,
      filePath,
      (progress, uploadTask) => {
        attachment.setProgress(progress);
        attachment.setUploadStatus(EAttachmentUploadStatus.IN_PROGRESS);
        attachment.setUploadTask(uploadTask);
      },
      (error) => {
        attachment.setUploadStatus(EAttachmentUploadStatus.ERROR);
        onError(error);
      },
      (downloadUrl) => {
        attachment.setUrl(downloadUrl);
        attachment.setUploadStatus(EAttachmentUploadStatus.FINISHED);
        if (onFinish) onFinish();
      }
    );
    return attachment;
  }

  public removeAttachmentFromStorage(
    contractUid: string,
    attachment: AttachmentModel
  ) {
    const filePath = this.getFilePath(contractUid, attachment);
    return FileService.deleteFile(filePath);
  }

  public async removeAllAttachmentsFromStorage(contractUid: string) {
    const data = { contractUid };
    await axios.post(`${process.env.REACT_APP_DELETE_ATTACHMENTS}`, data);
  }

  public async validateAttachment(file: File) {
    const allowedTypes = [jpgMime, pdfMime, txtMime, docxMime, xlsxMime];
    if (!allowedTypes.includes(file.type)) {
      throw new InvalidAttachmentFormatException();
    }

    const maximumSize = 105906176; // 101MB
    if (file.size > maximumSize) {
      throw new MaximumAttachmentSizeException();
    }

    const isValid = await this.validateFile(file);
    if (!isValid) {
      throw new InvalidAttachmentException();
    }
  }

  public async applyAttachments(
    contract: ContractModel,
    attachments: AttachmentModel[],
    attachmentsToExclude: AttachmentModel[],
    updateCallback: (
      attachmentsUids: string[],
      transaction: Transaction
    ) => Promise<void>
  ): Promise<string[]> {
    if (attachments.length > 20) {
      throw new Error("Maximum number of attachments exceeded");
    }

    const attUids = attachments.map((a) => a.uid);
    const addedAttachments = attachments.filter((att) => !att.isSaved());
    const removedAttachmnts = await this.filterAttachmentsThatCanBeDeleted(
      contract.uid,
      attachmentsToExclude
    );

    await TransactionService.run(async (transaction) => {
      const promisesAdd = addedAttachments.map((attachment) =>
        AttachmentRepository.addAttachmentToDataBase(
          contract.uid,
          attachment.uid,
          attachment,
          transaction
        )
      );
      await Promise.all(promisesAdd);
      await updateCallback(attUids, transaction);

      const promisesRemove = removedAttachmnts.map((attachment) =>
        this.removeAttachmentFromContract(contract.uid, attachment, transaction)
      );
      await Promise.all(promisesRemove);
    });

    addedAttachments.forEach((attachment) => attachment.setIsSaved(true));
    return attUids;
  }

  private getFilePath(contractUid: string, attachment: AttachmentModel) {
    return `faturamento/${contractUid}/${attachment.attachedTo}/${attachment.realName}`;
  }

  private createAttachmentFromFile(
    file: File,
    attachedTo: EAttachmentType
  ): AttachmentModel {
    const attachmentId = uuidFirebase();
    const attachment = AttachmentModel.createFromFile(
      file,
      attachmentId,
      attachedTo
    );
    return attachment;
  }

  public async removeAttachmentFromContract(
    contractUid: string,
    attachment: AttachmentModel,
    transaction?: Transaction
  ) {
    await AttachmentRepository.deleteAttachmentFromDataBase(
      contractUid,
      attachment.uid,
      transaction
    );
    await this.removeAttachmentFromStorage(contractUid, attachment).catch(() =>
      console.error("Error deleting attachment from storage")
    );
  }

  private async filterAttachmentsThatCanBeDeleted(
    contractUid: string,
    attachments: AttachmentModel[]
  ): Promise<AttachmentModel[]> {
    if (!attachments.length) {
      return [];
    }

    // Essa validação só é necessária para os anexos de movimentações dos serviços
    // porque é possível usar o mesmo anexo para diversos movimentos de serviços diferentes
    if (attachments[0].attachedTo !== EAttachmentType.MOVIMENTS) {
      return attachments;
    }

    const promises = attachments.map((attachment) =>
      this.canDeleteAttachment(contractUid, attachment)
    );
    const result = await Promise.all(promises);

    return result.filter((attachment) =>
      Boolean(attachment)
    ) as AttachmentModel[];
  }

  private async canDeleteAttachment(
    contractUid: string,
    attachment: AttachmentModel
  ): Promise<AttachmentModel | undefined> {
    const hasMoviments =
      await ContractRowMovimentService.hasMoreThanOneMovementWithAttachmentUid(
        contractUid,
        attachment.uid
      );
    return hasMoviments ? undefined : attachment;
  }

  private async validateFile(file: File) {
    switch (file.type) {
      case jpgMime:
        return await this.validateImageFile(file);
      case pdfMime:
        return await this.validatePDFFile(file);
      case txtMime:
        return await this.validateTXTFile(file);
      case docxMime:
        return await this.validateDOCXFile(file);
      case xlsxMime:
        return await this.validateXLSXFile(file);
      default:
        return false;
    }
  }

  private async validateDOCXFile(file: File) {
    try {
      const buffer = await file.arrayBuffer();
      await JSZip.loadAsync(buffer);
      return true;
    } catch (error) {
      return false;
    }
  }

  private async validateXLSXFile(file: File) {
    try {
      const service = new ExcelService();
      const worksheets = await service.readTabsFromFile(file);
      return Boolean(worksheets.length);
    } catch (error) {
      return false;
    }
  }

  private async validatePDFFile(file: File) {
    try {
      const buffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(buffer);
      const pages = pdfDoc.getPages();
      return Boolean(pages.length);
    } catch (error) {
      return false;
    }
  }

  private validateTXTFile(file: File) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(true);
      reader.onerror = () => resolve(false);
      reader.readAsDataURL(file);
    });
  }

  private validateImageFile(file: File) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function (event) {
        const fileContent = event.target?.result;
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = String(fileContent);
      };
      reader.onerror = () => resolve(false);
      reader.readAsDataURL(file);
    });
  }
}

export default new AttachmentService();
